/**
 * ONE word per COUNTER, derived once, read by all three of its consumers.
 *
 * `docs/sunmi-device-states.html` is the inventory: seven independent axes, and
 * a device that is paired, alive, screenless, holding a staged build and
 * standing in a paused shop satisfies five of them at the same time. Nothing
 * reads seven axes. Three things read the counter, and each asks one question:
 *
 *  - DISPATCH - may this counter be given an order right now?
 *  - THE OPERATOR - which shop needs a phone call, and which is fine?
 *  - ALERTING - who is told, and can they do anything about it?
 *
 * The unit is the COUNTER and not the device, which is the whole reason this is
 * not `FleetDeviceStatus` (`../internal`, seven values). A shop can run a Sunmi
 * beside a browser tablet and accept all morning on either; the fleet list is
 * per device and is an operator's tool for a rollout, not an answer to any of
 * the three questions above. A counter is ready if ANY of its devices is.
 *
 * Derived, never stored - the same reason an order has four regions and no status
 * column (PRD 4.2). Two screens computing this word independently is exactly
 * how the admin dashboard and the alert list came to disagree about one shop,
 * and it is why the grace constants below live here rather than in the sweep
 * that used to own them: the fleet list (OPS-029), the missing-heartbeat alert
 * (OPS-011), dispatch (ALC-012) and the partner's Devices dialog all judge one
 * silence against one window.
 */

import { z } from 'zod';

/** An instant as either side of the wire holds it: an ISO string, or a Date. */
type Instant = Date | string | number;

function msOf(instant: Instant): number {
  if (typeof instant === 'number') return instant;
  return instant instanceof Date ? instant.getTime() : Date.parse(instant);
}

/**
 * BAK-020 / OPS-011: how long a counter may go quiet before it counts as
 * missing.
 *
 * It lives beside the readiness word that USES it, because it is asked four
 * times now - by the heartbeat sweep, by the fleet Devices list, which calls a
 * device ONLINE on exactly this window (OPS-029), by dispatch and by the
 * partner's own Devices dialog. A second literal in the second reader is how
 * one screen comes to say a counter is online while the alert list says it went
 * dark.
 */
export const TABLET_HEARTBEAT_GRACE_MS = 10 * 60_000;

/**
 * The same window for a counter whose APP IS CLOSED (BAK-059, founder decision
 * B, 2026-08-30).
 *
 * Twenty-five minutes, and the number is arithmetic rather than taste: a closed
 * app beats from a periodic `JobScheduler` job, and fifteen minutes is
 * Android's floor for one (API 24) - anything shorter is silently raised to it.
 * So the quiet stretch between two healthy job beats is fifteen minutes plus
 * whatever the scheduler adds for doze and batching, which is already past the
 * ten-minute window an open app is judged on. Every closed counter therefore
 * crossed that window between every pair of beats and flapped ONLINE - ASLEEP -
 * ONLINE all night, raising OPS-011 on each stretch. And "fifteen" is the
 * PERIOD, not the gap: a periodic job runs anywhere inside its flex window,
 * which the handheld pins at five minutes (UpdateJob.FLEX_MS), so two healthy
 * job beats can be twenty minutes apart - measured at twenty-seven on a real
 * counter on 2026-08-30 before the flex was pinned, with the list calling it
 * ASLEEP. Twenty-five is that worst gap plus five of slack for doze and
 * batching, and still calls a counter dark inside half an hour.
 */
export const TABLET_CLOSED_HEARTBEAT_GRACE_MS = 25 * 60_000;

/**
 * HOW LONG THIS COUNTER MAY BE QUIET - the one decision, stated once.
 *
 * A silence means nothing on its own; it means something against the CADENCE
 * the last beat was sent at, and the handheld has two. Its on-screen ticker
 * beats every thirty seconds while any of its screens is in front; its periodic
 * job beats every fifteen minutes with the app closed, so a release lands on a
 * counter nobody opened. One grace window cannot serve both, and the beat
 * itself is what says which cadence it came from (`screenless` on the heartbeat
 * body, `tablet_devices.last_beat_screenless` in the row).
 */
export function heartbeatGraceMs(screenless: boolean): number {
  return screenless ? TABLET_CLOSED_HEARTBEAT_GRACE_MS : TABLET_HEARTBEAT_GRACE_MS;
}

/**
 * Has this counter been quiet longer than its last beat's own cadence allows?
 *
 * A counter never seen at all is missing by definition - there is no beat to
 * take a cadence from, and "never" is the longest silence there is.
 *
 * The comparison is `>=`, so the instant the window closes the counter is
 * missing rather than one millisecond later. That boundary is pinned by test
 * because both readers subtract from it.
 */
export function heartbeatMissing(
  lastSeen: Instant | null,
  screenless: boolean,
  now: Instant,
): boolean {
  if (lastSeen === null) return true;
  return msOf(now) - msOf(lastSeen) >= heartbeatGraceMs(screenless);
}

/**
 * THE BEAT THAT ANSWERS FOR A SHOP with several devices: of each device's own
 * last beat, the one that keeps the counter reachable longest - the latest
 * `at + heartbeatGraceMs(screenless)`. Null for none.
 *
 * "A counter is ready if ANY of its devices is" (the header above), and this
 * is what makes the shop's word say so. The shop used to be judged on its
 * single LATEST beat, whatever cadence it came on: a screen beat twelve
 * minutes old (judged on ten) beside a second device's background-job beat
 * fifteen minutes old (judged on twenty-five) read Offline, while the device
 * rows under it - each judged on its own beat - read the second one Online and
 * counted it in the green dot (#2004). Choosing by the instant each
 * beat stops vouching for its device means `heartbeatMissing` on the chosen
 * beat is true exactly when it is true of EVERY device's beat, whatever `now`
 * is - so every reader of a shop's beat (the readiness word, OPS-011, the
 * unattended sweep, dispatch) agrees with its device rows.
 *
 * A tie goes to the later instant, so a shop whose devices all beat on one
 * cadence still reports its most recent contact.
 */
export function liveliestBeat<B extends { readonly at: Instant; readonly screenless: boolean }>(
  beats: Iterable<B>,
): B | null {
  let chosen: B | null = null;
  let chosenUntil = Number.NEGATIVE_INFINITY;
  for (const beat of beats) {
    const until = msOf(beat.at) + heartbeatGraceMs(beat.screenless);
    if (
      chosen === null ||
      until > chosenUntil ||
      (until === chosenUntil && msOf(beat.at) > msOf(chosen.at))
    ) {
      chosen = beat;
      chosenUntil = until;
    }
  }
  return chosen;
}

/**
 * The five words, in the order they are decided: the first that holds is the
 * most actionable thing anybody can say about the shop.
 *
 * Suspension is deliberately NOT one of them. A suspended house is excluded by
 * OPS-003 before any of this is asked, and it is a fact about the ORG rather
 * than about the counter standing in it - a sixth value would be a second place
 * that decides whether a suspended shop trades.
 */
export const COUNTER_READINESS = [
  'NOT_EQUIPPED',
  'PAUSED',
  'UNREACHABLE',
  'STANDBY',
  'READY',
] as const;

export type CounterReadiness = (typeof COUNTER_READINESS)[number];

/**
 * The same five, as a wire schema, so a response shape can declare the field.
 *
 * It lives beside the list rather than in one mount's shapes because THREE
 * mounts declare it now - the Admin dashboard row (`internal`), the pairing
 * dialog both an operator and a house admin read (`device-pairing`, which
 * `internal` itself imports, so it cannot import back) - and a second
 * `z.enum([...])` spelling the five out again is a second definition of them.
 * `internal` re-exports this under the name its shapes already use.
 */
export const CounterReadinessSchema = z.enum(COUNTER_READINESS);

/** The counter's own facts, as every surface already carries them on the wire. */
export interface CounterReadinessInput {
  /**
   * How many paired, ACTIVE devices this counter has.
   *
   * `null` is reserved for the liveness-only reader of existing offers and
   * local scripted rehearsals. Deployed allocation supplies a grouped count.
   */
  readonly activeDevices: number | null;
  /** The most recent beat from ANY of this counter's devices; null = never. */
  readonly lastBeatAt: Instant | null;
  /** Whether that beat came from the periodic job rather than from a screen. */
  readonly lastBeatScreenless: boolean;
  /** The shop is not selling: paused by the baker (BAK-006) or by BAK-038. */
  readonly paused: boolean;
  readonly now: Instant;
}

/**
 * THE decision. Five values, decided in order, and the order is the point.
 *
 * A counter can be un-equipped AND paused AND silent all at once, and saying
 * "silent" about a shop that was never installed sends an operator to the phone
 * for an onboarding row (BAK-037). Each rule therefore answers only for the
 * counters the rules above it did not claim.
 */
export function counterReadinessOf(input: CounterReadinessInput): CounterReadiness {
  // Not installed yet: an onboarding fact on the roster, never an outage. A
  // shop can be created on Monday and equipped on Thursday.
  if (input.activeDevices === 0) return 'NOT_EQUIPPED';

  // Not selling. Whichever of the two triggers paused it, the pause and its
  // reason are what an operator reads - the silence underneath is not news.
  if (input.paused) return 'PAUSED';

  // Nobody can see this counter. The one that needs a phone call.
  if (heartbeatMissing(input.lastBeatAt, input.lastBeatScreenless, input.now)) {
    return 'UNREACHABLE';
  }

  // Alive, nobody looking - what a shut shop looks like at 22:00. Still
  // eligible: the arrival alarm rings from the watch service whether or not a
  // screen is in front, so an order placed now is heard.
  if (input.lastBeatScreenless) return 'STANDBY';

  return 'READY';
}

/**
 * Liveness-only reading for existing offers and local scripted rehearsals.
 * Missing history does not prove a previously offered counter went offline.
 * Deployed allocation counts paired devices separately (counter-liveness.ts)
 * and uses counterReadinessOf, so this fallback cannot admit unequipped shops
 * in production. Demo Robobaker has no physical heartbeat to synthesize.
 */
export function dispatchReadinessOf(input: {
  readonly lastBeatAt: Instant | null;
  readonly lastBeatScreenless: boolean;
  readonly now: Instant;
}): CounterReadiness {
  if (input.lastBeatAt === null) return 'READY';
  return counterReadinessOf({
    activeDevices: null,
    lastBeatAt: input.lastBeatAt,
    lastBeatScreenless: input.lastBeatScreenless,
    paused: false,
    now: input.now,
  });
}

/**
 * DOES THE ROBOBAKER ANSWER FOR THIS COUNTER (OPS-035) - the one decision, stated
 * once and asked by both readers: the counter-health roster that drives the robot
 * and the Internal row, and the readiness overlay dispatch allocates on.
 *
 * It does when the instance runs a robot at all, the shop trades, and no paired
 * device is REACHABLE - none installed, or every one silent past its own beat's
 * grace (founder, 2026-10-20: "if the physical Sunmi is off, the robobaker
 * emulates an active Sunmi"). Until that day the robot stood in only for a shop
 * with no device, so a demo house whose Sunmi was switched off read UNREACHABLE
 * and sold nothing for the day. The moment a device beats again the robot steps
 * back, because this is read fresh on every pass.
 */
export function robobakerCovers(input: {
  readonly enabled: boolean;
  readonly active: boolean;
  readonly activeDevices: number;
  readonly lastBeatAt: Instant | null;
  readonly lastBeatScreenless: boolean;
  readonly now: Instant;
}): boolean {
  return (
    input.enabled &&
    input.active &&
    (input.activeDevices === 0 ||
      heartbeatMissing(input.lastBeatAt, input.lastBeatScreenless, input.now))
  );
}

/**
 * May dispatch give this counter an order?
 *
 * A `switch` over every member with a `never` default rather than a list of the
 * two that pass: a sixth readiness value must be a typecheck failure here, not
 * a silent fall-through to whatever the last `else` said. The fall-through IS
 * the bug - it decides, in silence, whether a guest's morning is sold to a shop
 * nobody can reach.
 *
 * STANDBY passes for the reason above: screenless is not deaf.
 */
export function isCounterDispatchable(readiness: CounterReadiness): boolean {
  switch (readiness) {
    case 'READY':
    case 'STANDBY':
      return true;
    case 'NOT_EQUIPPED':
    case 'PAUSED':
    case 'UNREACHABLE':
      return false;
    default: {
      const exhaustive: never = readiness;
      return exhaustive;
    }
  }
}

/** The dashboard and allocation agree: no active device means no orders. */
export function counterTakesOrders(readiness: CounterReadiness): boolean {
  return isCounterDispatchable(readiness);
}
